import { useState, useEffect, useCallback, Fragment } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { FileText, Download, AlertCircle, Loader2, CreditCard, FileSignature, CheckCircle2, Lock } from "lucide-react";
import axios from "axios";
import logo from "@/assets/logo.png";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ROSE = "#b8977e";

// ===== Step Indicator =====
const StepIndicator = ({ current }) => {
  const steps = [
    { num: 1, label: "Acompte", icon: CreditCard },
    { num: 2, label: "Contrat", icon: FileSignature },
    { num: 3, label: "Devis", icon: FileText },
  ];
  return (
    <div className="flex items-center justify-center gap-2 mb-8 max-w-md mx-auto" data-testid="step-indicator">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isDone = current > s.num;
        const isActive = current === s.num;
        const color = isDone || isActive ? ROSE : "#e8d5c4";
        return (
          <Fragment key={s.num}>
            <div className="flex flex-col items-center gap-1" data-testid={`step-${s.num}-indicator`}>
              <div
                style={{
                  width: 38, height: 38, borderRadius: "50%",
                  background: isDone || isActive ? ROSE : "#fff",
                  border: `2px solid ${color}`,
                  color: isDone || isActive ? "#fff" : "#b8977e",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 600, fontSize: 14, transition: "all .3s",
                }}
              >
                {isDone ? "✓" : <Icon className="w-4 h-4" />}
              </div>
              <span style={{ fontSize: 11, color: isDone || isActive ? ROSE : "#9a9a9a", fontWeight: 600 }}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: 2, marginBottom: 18,
                background: current > s.num ? ROSE : "#e8d5c4",
                transition: "all .3s",
              }} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

const SignatureQuotePage = () => {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1 — Deposit
  const [loadingDeposit, setLoadingDeposit] = useState(false);
  const [verifyingDeposit, setVerifyingDeposit] = useState(false);

  // Step 2 — Contract
  const [contractFirst, setContractFirst] = useState("");
  const [contractLast, setContractLast] = useState("");
  const [contractAccepted, setContractAccepted] = useState(false);
  const [signingContract, setSigningContract] = useState(false);

  // Step 3 — Quote
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const today = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const canSignQuote = firstName.trim().length > 0 && lastName.trim().length > 0 && accepted;
  const canSignContract = contractFirst.trim().length > 0 && contractLast.trim().length > 0 && contractAccepted;

  const fetchQuote = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quotes/${token}`);
      setQuote(res.data);
      if (res.data.quote_status === "signed") {
        setSigned(true);
        setCurrentStep(3);
      } else if (res.data.contract_signed) {
        setCurrentStep(3);
      } else if (res.data.deposit_paid) {
        setCurrentStep(2);
      } else {
        setCurrentStep(1);
      }
    } catch (err) {
      setError(err.response?.status === 410 ? "expired" : "not_found");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  // Detect Stripe redirect → verify deposit
  useEffect(() => {
    const depositPaidParam = searchParams.get("deposit_paid");
    const sessionId = searchParams.get("session_id");
    if (depositPaidParam === "true" && sessionId && !verifyingDeposit) {
      setVerifyingDeposit(true);
      axios.get(`${API}/checkout/deposit-status/${sessionId}`)
        .then(() => {
          // Clear query params + refresh quote
          searchParams.delete("deposit_paid");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          fetchQuote();
        })
        .catch(() => {})
        .finally(() => setVerifyingDeposit(false));
    }
  }, [searchParams, setSearchParams, fetchQuote, verifyingDeposit]);

  const handlePayDeposit = async () => {
    setLoadingDeposit(true);
    try {
      const res = await axios.post(`${API}/quotes/${token}/create-deposit-checkout`);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      alert(extractErrorMessage(err, "Erreur lors de la création du paiement."));
      setLoadingDeposit(false);
    }
  };

  const handleSignContract = async () => {
    if (!canSignContract) return;
    setSigningContract(true);
    try {
      await axios.post(`${API}/quotes/${token}/sign-contract`, {
        first_name: contractFirst.trim(),
        last_name: contractLast.trim(),
      });
      await fetchQuote();
      setCurrentStep(3);
    } catch (err) {
      alert(extractErrorMessage(err, "Erreur lors de la signature du contrat."));
    } finally { setSigningContract(false); }
  };

  const handleSignQuote = async () => {
    if (!canSignQuote) return;
    setSigning(true);
    try {
      await axios.post(`${API}/quotes/${token}/sign`, {
        signed_by_first: firstName.trim(),
        signed_by_last: lastName.trim(),
      });
      setSigned(true);
      fetchQuote();
    } catch (err) {
      alert(extractErrorMessage(err, "Erreur lors de la signature."));
    } finally { setSigning(false); }
  };

  const downloadPdf = () => window.open(`${API}/quotes/${token}/pdf`, "_blank");
  const downloadContract = () => window.open(`${API}/quotes/${token}/contract-pdf`, "_blank");

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-['DM_Sans']">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#b8977e] mx-auto mb-4" />
          <p className="text-[#666]">Chargement du devis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-['DM_Sans']">
        <div className="text-center max-w-md px-6">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1a1a1a] mb-2">
            {error === "expired" ? "Devis expiré" : "Devis introuvable"}
          </h1>
          <p className="text-[#666] mb-6">
            {error === "expired"
              ? "Ce devis a expiré (plus de 30 jours). Veuillez nous contacter."
              : "Ce lien de devis n'est pas valide."}
          </p>
          <Link to="/" className="inline-block bg-[#b8977e] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#a68770] transition-colors">
            Retour
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] font-['DM_Sans']" data-testid="signature-page">
      <nav className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/"><img src={logo} alt="SelfieBooth Event" style={{ height: 48, width: "auto" }} /></Link>
          <span className="text-sm text-[#999]">Devis N°{quote.quote_number}</span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1a1a1a] font-['Playfair_Display']">
            Validation de votre réservation
          </h1>
          <p className="text-[#666] text-sm mt-2">
            3 étapes simples pour confirmer votre événement.
          </p>
        </div>

        <StepIndicator current={currentStep} />

        {/* Quote summary card — always visible */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#e8d5c4] p-5 mb-6" data-testid="quote-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#b8977e] font-semibold">Devis</p>
              <p className="font-bold text-[#1a1a1a]">{quote.package?.name} — {quote.package?.duration}</p>
              <p className="text-xs text-[#999] mt-0.5">
                {quote.event_date} · {quote.event_location}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#999]">Total</p>
              <p className="text-xl font-bold text-[#b8977e]" data-testid="quote-total">{quote.total_price?.toFixed(2)} €</p>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-3 border-t border-[#f0e4da] text-xs text-[#666]">
            <button onClick={downloadPdf} className="hover:text-[#b8977e] inline-flex items-center gap-1" data-testid="download-pdf-btn">
              <Download className="w-3.5 h-3.5" /> Devis PDF
            </button>
            {quote.contract_signed && (
              <button onClick={downloadContract} className="hover:text-[#b8977e] inline-flex items-center gap-1" data-testid="download-contract-btn">
                <Download className="w-3.5 h-3.5" /> Contrat signé PDF
              </button>
            )}
          </div>
        </div>

        {/* ===== STEP 1 — DEPOSIT ===== */}
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#e8d5c4] p-6 sm:p-8" data-testid="step-1-deposit">
            <h2 className="text-xl font-bold text-[#1a1a1a] font-['Playfair_Display'] mb-2">
              Étape 1 — Acompte de réservation
            </h2>
            <p className="text-[#666] text-sm mb-5">
              Un acompte de <strong style={{ color: ROSE }}>30%</strong> est requis pour confirmer votre réservation.
              Le solde de 70% sera à régler avant l'événement.
            </p>

            <div className="rounded-xl border-2 border-[#b8977e] bg-[#faf8f5] p-5 mb-6">
              <div className="flex justify-between mb-2 text-sm">
                <span className="text-[#666]">Total réservation</span>
                <span className="font-semibold">{quote.total_price?.toFixed(2)} €</span>
              </div>
              <div className="border-t border-[#e8d5c4] pt-3 mt-3">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-[#b8977e] font-semibold">Acompte aujourd'hui (30%)</span>
                  <span className="text-2xl font-bold text-[#b8977e]" data-testid="deposit-amount">
                    {quote.deposit_amount?.toFixed(2)} €
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#999]">Solde restant (70%)</span>
                  <span className="text-[#999]" data-testid="balance-amount">{quote.balance_amount?.toFixed(2)} €</span>
                </div>
              </div>
            </div>

            <button
              onClick={handlePayDeposit}
              disabled={loadingDeposit || verifyingDeposit}
              className="w-full font-semibold text-white py-4 rounded-full text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              style={{
                background: loadingDeposit || verifyingDeposit ? "#ccc" : `linear-gradient(135deg, ${ROSE} 0%, #a68770 100%)`,
                boxShadow: !loadingDeposit && !verifyingDeposit ? "0 4px 20px rgba(184,151,126,0.3)" : "none",
              }}
              data-testid="pay-deposit-btn"
            >
              {loadingDeposit ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Redirection vers Stripe...</>
              ) : verifyingDeposit ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Vérification du paiement...</>
              ) : (
                <><Lock className="w-4 h-4" /> Payer l'acompte {quote.deposit_amount?.toFixed(2)} €</>
              )}
            </button>
            <p className="text-center text-xs text-[#999] mt-2">
              Paiement sécurisé par Stripe · Visa / Mastercard / CB
            </p>
          </div>
        )}

        {/* ===== STEP 2 — CONTRACT ===== */}
        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#e8d5c4] p-6 sm:p-8" data-testid="step-2-contract">
            <h2 className="text-xl font-bold text-[#1a1a1a] font-['Playfair_Display'] mb-2">
              Étape 2 — Contrat de location
            </h2>
            <p className="text-[#666] text-sm mb-5">
              Lisez attentivement le contrat avant de le signer.
            </p>

            <div
              data-testid="contract-text"
              style={{
                border: "1px solid #e8d5c4", borderRadius: 12, height: 380,
                overflowY: "scroll", padding: 20, background: "#faf8f5",
                marginBottom: 16, fontSize: 13, lineHeight: 1.7, color: "#2d2319",
              }}
            >
              <h3 className="text-center font-['Playfair_Display'] font-bold mb-4 text-base">
                CONTRAT DE LOCATION — PHOTOBOOTH
              </h3>
              <p className="mb-3"><strong>LOUEUR :</strong> SelfieBooth Event — Walid Bourahla<br/>
                44B Rue Gabriel Péri, 78210 Saint-Cyr-l'École<br/>
                SIRET : 93145736000010</p>
              <p className="mb-3"><strong>LOCATAIRE :</strong> {quote.customer_name}<br/>
                Email : {quote.customer_email}</p>
              <hr style={{ borderColor: "#e8d5c4", margin: "12px 0" }} />
              <p className="mb-3"><strong>Article 1 — Matériel mis à disposition</strong><br/>
                Borne photo tactile, imprimante thermique, éclairage studio, accessoires & props inclus, assistance technique à distance.</p>
              <p className="mb-3"><strong>Article 2 — Responsabilité du locataire</strong><br/>
                Le locataire est responsable du matériel pendant toute la durée de l'événement. Il s'engage à ne pas déplacer le matériel, surveiller son bon usage par les invités et signaler tout incident immédiatement.</p>
              <p className="mb-3"><strong>Article 3 — Dommages et casse</strong><br/>
                En cas de dégradation imputable au locataire ou à ses invités :<br/>
                • Écran tactile endommagé : <strong>400 €</strong><br/>
                • Imprimante endommagée : <strong>350 €</strong><br/>
                • Borne / structure endommagée : <strong>500 €</strong><br/>
                • Accessoires perdus / endommagés : <strong>50 €</strong><br/>
                • Destruction totale : valeur de remplacement au prix du marché<br/>
                Le loueur peut facturer les dommages dans les 7 jours suivant l'événement.</p>
              <p className="mb-3"><strong>Article 4 — Caution</strong><br/>
                Une caution de <strong>300 €</strong> est due le jour de l'installation. Elle est restituée intégralement sous 48h si aucun dommage n'est constaté.</p>
              <p className="mb-3"><strong>Article 5 — Annulation</strong><br/>
                • Plus de 30 jours avant : remboursement acompte 100%<br/>
                • Entre 15 et 30 jours : remboursement acompte 50%<br/>
                • Moins de 15 jours : acompte conservé intégralement<br/>
                • Le jour J : aucun remboursement</p>
              <p className="mb-3"><strong>Article 6 — Force majeure</strong><br/>
                En cas de force majeure avérée, report sans pénalité pour aucune des parties.</p>
              <p><strong>Article 7 — Droit applicable</strong><br/>
                Contrat soumis au droit français. Juridiction compétente : Saint-Cyr-l'École (78).</p>
            </div>

            <button
              onClick={downloadContract}
              className="text-[#b8977e] hover:text-[#a68770] text-sm font-medium inline-flex items-center gap-1.5 mb-5"
              data-testid="download-contract-step2"
            >
              <Download className="w-4 h-4" /> Télécharger le contrat en PDF
            </button>

            <div className="rounded-xl border border-[#e8d5c4] bg-[#faf8f5] p-5 mb-5">
              <h4 className="font-['Playfair_Display'] font-semibold text-[#2d2319] mb-3">Votre signature</h4>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#b8977e] font-semibold mb-1">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    value={contractFirst}
                    onChange={(e) => setContractFirst(e.target.value)}
                    placeholder="Votre prénom"
                    className="w-full border-[1.5px] border-[#e8d5c4] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b8977e]/30 focus:border-[#b8977e] transition"
                    data-testid="contract-firstname"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-[#b8977e] font-semibold mb-1">
                    Nom *
                  </label>
                  <input
                    type="text"
                    value={contractLast}
                    onChange={(e) => setContractLast(e.target.value)}
                    placeholder="Votre nom"
                    className="w-full border-[1.5px] border-[#e8d5c4] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#b8977e]/30 focus:border-[#b8977e] transition"
                    data-testid="contract-lastname"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-[11px] uppercase tracking-wider text-[#b8977e] font-semibold mb-1">
                  Date de signature
                </label>
                <input
                  type="text"
                  value={today}
                  readOnly
                  className="w-full border-[1.5px] border-[#e8d5c4] rounded-lg px-3 py-2.5 text-sm bg-white text-[#999] cursor-not-allowed"
                />
              </div>

              {(contractFirst || contractLast) && (
                <div className="rounded-lg border-2 border-[#b8977e]/40 bg-white p-4 text-center mb-3" data-testid="contract-signature-preview">
                  <p className="text-[#b8977e] text-xs italic mb-0.5">Bon pour accord</p>
                  <p className="font-['Playfair_Display'] text-lg font-bold text-[#1a1a1a]">
                    {contractFirst} {contractLast.toUpperCase()}
                  </p>
                  <p className="text-[#999] text-xs">Le {today}</p>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={contractAccepted}
                  onChange={(e) => setContractAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#b8977e] rounded"
                  data-testid="contract-accept-checkbox"
                />
                <span className="text-xs text-[#666] leading-relaxed">
                  J'ai lu et j'accepte le contrat de location ci-dessus. Je m'engage à respecter les
                  conditions générales et à être responsable du matériel mis à disposition.
                  Mention <strong>« Bon pour accord »</strong>.
                </span>
              </label>
            </div>

            <button
              onClick={handleSignContract}
              disabled={!canSignContract || signingContract}
              className="w-full font-semibold text-white py-4 rounded-full text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              style={{
                background: canSignContract ? `linear-gradient(135deg, ${ROSE} 0%, #a68770 100%)` : "#ccc",
                boxShadow: canSignContract ? "0 4px 20px rgba(184,151,126,0.3)" : "none",
              }}
              data-testid="sign-contract-btn"
            >
              {signingContract ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signature en cours...</>
              ) : (
                <><FileSignature className="w-4 h-4" /> Signer le contrat de location</>
              )}
            </button>
          </div>
        )}

        {/* ===== STEP 3 — QUOTE ===== */}
        {currentStep === 3 && !signed && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#e8d5c4] p-6 sm:p-8" data-testid="step-3-quote">
            <div className="bg-[#f0fdf4] border border-[#86efac] rounded-lg p-3 mb-5 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#2d7a4f] flex-shrink-0" />
              <span className="text-sm text-[#2d7a4f]">
                Acompte payé et contrat signé. Signez maintenant votre devis pour finaliser.
              </span>
            </div>

            <h2 className="text-xl font-bold text-[#1a1a1a] font-['Playfair_Display'] mb-2">
              Étape 3 — Signature du devis
            </h2>
            <p className="text-[#666] text-sm mb-5">
              Remplissez les champs ci-dessous pour valider votre accord.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-[#555] mb-1">Prénom *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Votre prénom"
                  className="w-full border-[1.5px] border-[#e8d5c4] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b8977e]/30 focus:border-[#b8977e] transition"
                  data-testid="sig-firstname"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#555] mb-1">Nom *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Votre nom"
                  className="w-full border-[1.5px] border-[#e8d5c4] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b8977e]/30 focus:border-[#b8977e] transition"
                  data-testid="sig-lastname"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-[#555] mb-1">Date de signature</label>
              <input
                type="text"
                value={today}
                readOnly
                className="w-full border-[1.5px] border-[#e8d5c4] rounded-xl px-4 py-3 text-sm bg-[#fafafa] text-[#999] cursor-not-allowed"
                data-testid="sig-date"
              />
            </div>

            {(firstName || lastName) && (
              <div className="mb-5 rounded-xl border-2 border-[#b8977e]/40 bg-[#faf8f5] p-5 text-center" data-testid="sig-preview">
                <p className="text-[#b8977e] text-sm font-medium italic mb-1">Bon pour accord</p>
                <p className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a]">
                  {firstName} {lastName.toUpperCase()}
                </p>
                <p className="text-[#999] text-xs mt-1">Le {today}</p>
              </div>
            )}

            <label className="flex items-start gap-3 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-[#b8977e] rounded"
                data-testid="accept-checkbox"
              />
              <span className="text-sm text-[#666] leading-relaxed">
                <strong>Bon pour accord</strong> — J'accepte les conditions du devis et confirme vouloir réserver la prestation SelfieBooth Event pour la date indiquée.
              </span>
            </label>

            <button
              onClick={handleSignQuote}
              disabled={signing || !canSignQuote}
              className="w-full font-semibold text-white py-4 rounded-full text-base transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: canSignQuote ? `linear-gradient(135deg, ${ROSE} 0%, #a68770 100%)` : "#ccc",
                boxShadow: canSignQuote ? "0 4px 20px rgba(184,151,126,0.3)" : "none",
              }}
              data-testid="submit-signature-btn"
            >
              {signing ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Signature en cours...</span>
              ) : (
                "Valider et signer le devis"
              )}
            </button>
          </div>
        )}

        {/* ===== SUCCESS BANNER ===== */}
        {signed && (
          <div
            className="rounded-2xl p-8 text-center"
            style={{ background: "#faf8f5", border: "1px solid #e8d5c4", animation: "sigSuccessFade 320ms ease" }}
            data-testid="signed-banner"
          >
            <style>{`
              @keyframes sigSuccessFade { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-[#b8977e]" />
            </div>
            <h2 className="text-2xl mb-2 font-['Playfair_Display'] font-semibold text-[#b8977e]">
              Réservation confirmée !
            </h2>
            <p className="text-sm text-[#5a5a5a] mb-2">
              Acompte payé · Contrat signé · Devis signé
            </p>
            <p className="text-sm text-[#5a5a5a] mb-6">
              Un email de confirmation vous a été envoyé.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={downloadPdf}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-[#b8977e] text-white hover:bg-[#a68770] transition-colors"
                data-testid="download-signed-pdf"
              >
                <Download className="w-4 h-4" /> Devis signé
              </button>
              <button
                onClick={downloadContract}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-white text-[#b8977e] border border-[#b8977e] hover:bg-[#faf8f5] transition-colors"
                data-testid="download-signed-contract"
              >
                <Download className="w-4 h-4" /> Contrat signé
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className="bg-[#111] py-8 mt-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <img src={logo} alt="SelfieBooth Event" className="mx-auto mb-3" style={{ height: 32, width: "auto", filter: "brightness(0) invert(1)" }} />
          <p className="text-[#555] text-xs">© 2026 SelfieBooth Event. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default SignatureQuotePage;
